import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { PRIVILEGES } from 'constants/privileges';
import {
  useState,
  useCallback,
  MouseEvent,
  ChangeEvent,
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import { Divider, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { AlertService } from 'api';
import { Alert as AlertData } from 'api/alerts/alerts.types';
import {
  CustomTablePagination,
  StyledTableCell,
  StyledTableHeadCell,
} from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { format } from 'date-fns';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { InvoiceTab } from 'root/pathnames';
import { SortDirection } from 'types/invoices';
import { hasPrivilege } from 'utils/authFunctions.utils';
import { numberWithCommas } from 'utils/numberFunctions.utils';
import { Filters } from '../filters';
import { InvoicesTabs } from '../InvoicesTabs';
import { InvoiceTableActions } from './components/InvoicesTableActions';
import { InvoicesTableSummary } from './components/InvoicesTableSummary';
import { SaldeoErrorsList } from './components/SaldeoErrorsList/SaldeoErrorsList';
import * as S from './InvoicesTable.css';
import {
  InvoicesTableProps,
  HeadCell,
  TableData,
  SortProperties,
} from './invoicesTable.types';
import {
  getInvoiceDisplayedStatus,
  getKSeFCheckedStatus,
  getMarkedInvoiceHint,
  getSortProperties,
} from './InvoicesTable.utils';
import {
  ownHeadCells,
  foreignHeadCells,
  administrativeHeadCells,
} from './invoiceTable.const';

function createTableData(
  code: string,
  jobCode: string,
  client: string,
  saleDate: number,
  issueDate: number,
  dueDate: number,
  grossValue: string,
  status: string,
  displayedStatus: string,
  sellerCounterpartyName: string,
  logNumber: number,
  id: number,
  currency: string,
  isCorrective: boolean,
  paid: boolean,
  deleted: boolean,
  containsComplainedAllocations: boolean,
  containsUnrecognizedInvoiceLines: boolean,
  containsFailedAllocations: boolean,
  exportInformation: any,
  publicationDate: number,
  owner: any,
  ksefNumber: string | null,
): TableData {
  return {
    code,
    jobCode,
    client,
    saleDate: saleDate ? format(saleDate, DISPLAY_DATE_FORMAT) : '',
    issueDate: issueDate ? format(issueDate, DISPLAY_DATE_FORMAT) : '',
    dueDate: dueDate ? format(dueDate, DISPLAY_DATE_FORMAT) : '',
    grossValue,
    status,
    displayedStatus,
    sellerCounterpartyName,
    logNumber,
    id,
    currency,
    isCorrective,
    paid,
    deleted,
    containsComplainedAllocations,
    containsUnrecognizedInvoiceLines,
    containsFailedAllocations,
    exportInformation,
    publicationDate,
    owner,
    ksefNumber,
  };
}

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof TableData,
  ) => void;
  onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
  order?: Order;
  orderBy?: string;
  sortProperties: SortProperties;
  rowCount: number;
  cells: HeadCell[];
}

function EnhancedTableHead({
                             onSelectAllClick,
                             order,
                             orderBy,
                             numSelected,
                             rowCount,
                             onRequestSort,
                             cells,
                             sortProperties,
                           }: EnhancedTableProps) {
  const createSortHandler =
    (property: keyof TableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <StyledTableHeadCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'Select all invoices',
            }}
          />
        </StyledTableHeadCell>
        {cells.map((headCell) => {
          const isSortActive = orderBy === sortProperties[headCell.id];
          return (
            <StyledTableHeadCell
              key={headCell.id}
              align={headCell.align}
              padding="normal"
              sortDirection={isSortActive ? order : false}
            >
              <TableSortLabel
                active={isSortActive}
                direction={order}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === SortDirection.DESC
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </StyledTableHeadCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export function InvoicesTable({
                                data,
                                params,
                                setParams,
                                invoicesLoading,
                                onAction,
                              }: InvoicesTableProps) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<TableData[]>([]);
  const [saldeoErrors, setSaldeoErrors] = useState<AlertData[]>([]);
  const { tab: invoiceTab } = useParams<{ tab: InvoiceTab }>();

  const navigate = useNavigate();
  const location = useLocation();

  const { sortProperty, sortDirection } = params;

  const sortProperties = getSortProperties(invoiceTab !== InvoiceTab.FOREIGN);

  const rows = useMemo(
    () =>
      data.invoices.map((i) => {
        const isCorrective = i.invoiceClass.type
          .toLowerCase()
          .includes('corrective');

        return createTableData(
          i.code,
          i.createdFrom?.code,
          i.buyerCounterparty.code,
          i.saleDate,
          i.issueDate,
          i.dueDate,
          numberWithCommas(i.totals.grossValue) + ' ' + i.currencyCode.currency,
          i.status,
          getInvoiceDisplayedStatus(i, invoiceTab),
          i.sellerCounterparty.code,
          i.logNumber,
          i.id,
          i.currencyCode.currency,
          isCorrective,
          i.paid,
          i.deleted,
          !isCorrective && i.containsComplainedAllocations,
          !isCorrective &&
          i.invoiceLines.some(
            (i) => i.activity.discriminant === 'UNRECOGNIZED',
          ),
          !isCorrective &&
          i.invoiceLines.some((i) =>
            i.description?.includes('ALLOCATION_FAILED'),
          ),
          i.exportInformation,
          i.publicationDate,
          i.owner,
          getKSeFCheckedStatus(i),
        );
      }),
    [data],
  );

  const handleRequestSort = (
    _: MouseEvent<unknown>,
    property: keyof TableData,
  ) => {
    const sortProperty = sortProperties[property];
    const isAsc = sortDirection === SortDirection.ASC;
    setParams((prev) => ({
      ...prev,
      sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
      sortProperty,
    }));
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.filter((r) => !r.deleted));
      return;
    }
    setSelected([]);
  };

  const handleChecked = (event: MouseEvent<unknown>, id: TableData) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected: TableData[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setParams((prev) => ({
      ...prev,
      resultSize: parseInt(event.target.value, 10),
      pageNumber: 1,
    }));
  };

  const isSelected = (row: TableData) => selected.indexOf(row) !== -1;

  const tableHeaderCells: HeadCell[] = useMemo(() => {
    if (params.administrative) {
      return administrativeHeadCells;
    } else if (params.own) {
      return ownHeadCells;
    } else {
      return foreignHeadCells;
    }
  }, [params.own, params.administrative]);

  const fetchTabAlerts = useCallback(async () => {
    if (invoiceTab === InvoiceTab.INTERNAL) {
      setSaldeoErrors([]);
      return;
    }
    const keyword =
      invoiceTab === InvoiceTab.FOREIGN
        ? 'saldeo'
        : invoiceTab === InvoiceTab.OWN ||
        invoiceTab === InvoiceTab.ADMINISTRATIVE
          ? 'enova'
          : '';

    const res = await AlertService.getAlertsInbox();
    setSaldeoErrors(
      res.data.result.filter((a) =>
        a.message.toLowerCase().includes(keyword),
      ),
    );
  }, [invoiceTab]);

  const handleAction = async () => {
    await onAction();
    await fetchTabAlerts();
    setSelected([]);
  };

  const handleDismissSaldeoError = useCallback((id: number) => {
    setSaldeoErrors((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const handleRedirectToDetails = (row: TableData) => {
    hasPrivilege(PRIVILEGES.Invoices.VIEW_INVOICE_DETAILS, user) &&
    navigate(
      `${location.pathname}/${
        row.isCorrective ? 'correctiveinvoice' : 'invoice'
      }/${row.id}`,
    );
  };

  useEffect(() => {
    setSelected([]);
  }, [params]);

  useEffect(() => {
    fetchTabAlerts();
  }, [fetchTabAlerts]);

  return (
    <Box sx={{ width: '100%', maxWidth: '1524px' }}>
      <InvoiceTableActions
        selectedInvoices={selected}
        onAction={handleAction}
        onSaldeoErrors={setSaldeoErrors}
      />
      {invoiceTab !== InvoiceTab.INTERNAL && (
        <SaldeoErrorsList
          alerts={saldeoErrors}
          onDismiss={handleDismissSaldeoError}
        />
      )}
      <Paper sx={{ width: '100%', margin: '1rem 0' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            pl: { sm: 2 },
          }}
        >
          <InvoicesTabs />
        </Box>
        <Filters params={params} setParams={setParams} />
        <Divider />
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={data.count}
          rowsPerPage={params.resultSize}
          page={params.pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <Divider />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={sortDirection}
              orderBy={sortProperty}
              sortProperties={sortProperties}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.filter((r) => !r.deleted).length}
              cells={tableHeaderCells}
            />
            <TableBody>
              {rows.map((row) => {
                const isItemSelected = isSelected(row);
                const labelId = `enhanced-table-checkbox-${row.code}`;
                return (
                  <Fragment key={row.id}>
                    <S.InvoicesTableRow
                      key={row.id}
                      hover
                      $deleted={row.deleted && !invoicesLoading}
                      $highlighted={
                        row.containsComplainedAllocations && !invoicesLoading
                      }
                      $marked={
                        (row.containsUnrecognizedInvoiceLines ||
                          row.containsFailedAllocations) &&
                        !invoicesLoading
                      }
                      title={getMarkedInvoiceHint(row)}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{ cursor: 'pointer', height: '48px' }}
                    >
                      <TableCell
                        padding={!invoicesLoading ? 'checkbox' : 'normal'}
                      >
                        {!invoicesLoading ? (
                          <Checkbox
                            disabled={row.deleted}
                            color="primary"
                            checked={isItemSelected}
                            onClick={(event) => handleChecked(event, row)}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        ) : (
                          <Skeleton
                            variant="circular"
                            width="15px"
                            height="15px"
                          />
                        )}
                      </TableCell>
                      {tableHeaderCells.map((cell) => (
                        <StyledTableCell
                          key={cell.id}
                          align={cell.align || 'left'}
                          height="condensed"
                          $disabled={
                            !hasPrivilege(
                              PRIVILEGES.Invoices.VIEW_INVOICE_DETAILS,
                              user,
                            )
                          }
                          onClick={() => handleRedirectToDetails(row)}
                        >
                          {!invoicesLoading ? row[cell.id] : <Skeleton />}
                        </StyledTableCell>
                      ))}
                    </S.InvoicesTableRow>
                  </Fragment>
                );
              })}
              <InvoicesTableSummary
                selectedInvoices={selected}
                headerCells={tableHeaderCells}
                numSelected={selected.length}
                onSelectAllClick={handleSelectAllClick}
                rowCount={rows.filter((r) => !r.deleted).length}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={data.count}
          rowsPerPage={params.resultSize}
          page={params.pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <InvoiceTableActions
        selectedInvoices={selected}
        onAction={handleAction}
        onSaldeoErrors={setSaldeoErrors}
      />
    </Box>
  );
}